import * as React from "react";
import { UseQueryResult } from "react-query";
import { Model } from "../../types";

import Async from "./Async";
import Layout from "./Layout";

export interface ListProps<P = any> {
  items?: P[];
  renderItem: (props: { item: P; index: number }) => JSX.Element;
  getKey: (item: P, index: number) => Model["id"];
  renderEmpty?: () => JSX.Element;
  isEmpty?: (items: P[]) => boolean;
}

function List<P = any>({
  items = [],
  renderItem,
  getKey,
  renderEmpty,
  isEmpty: isEmptyProp,
}: ListProps<P>) {
  const isEmpty = isEmptyProp ? isEmptyProp(items) : !items?.length;

  return (
    <>
      {items?.map((item, index) => (
        <React.Fragment key={`${getKey(item, index)}`}>
          {renderItem({ item, index })}
        </React.Fragment>
      ))}

      {isEmpty && renderEmpty?.()}
    </>
  );
}

export interface ListItem {
  id: Model["id"];
}

export type AsyncListProps<P extends ListItem = ListItem> = UseQueryResult<
  P[],
  unknown
> &
  Omit<ListProps, "getKey" | "items">;

function AsyncList<P extends ListItem = ListItem>({
  renderItem,
  renderEmpty,
  isEmpty,
  ...asyncProps
}: AsyncListProps<P>) {
  return (
    <Async<P[]> {...asyncProps}>
      {({ data: items }) => (
        <List<P>
          items={items}
          getKey={(item) => `${item.id}`}
          renderItem={renderItem}
          renderEmpty={renderEmpty}
          isEmpty={isEmpty}
        />
      )}
    </Async>
  );
}

export interface ListItemProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  left?: React.ReactNode;
  right?: React.ReactNode;
}

function ListItem({ left, right, title, description }: ListItemProps) {
  return (
    <Layout.Row>
      {left}

      <Layout.Column>
        {title}
        {description}
      </Layout.Column>

      {right}
    </Layout.Row>
  );
}

List.Async = AsyncList;
List.Item = ListItem;

export default List;
